// ============================================================
// DM-LMS — Shared Types
// Single source of truth consumed by API and Web.
// DRY: define once here, import everywhere.
// ============================================================

// ---- Roles ----
export const ROLES = ["student", "tutor", "admin"] as const;
export type Role = (typeof ROLES)[number];

// ---- User ----
export interface User {
  id: string;
  code: string;          // ST-0001 | TU-0001 | AD-0001
  name: string;
  email: string;
  phone: string | null;
  role: Role;
  avatarUrl: string | null;
  createdAt: string;     // ISO-8601
  updatedAt: string;
}

// ---- Course ----
export const DELIVERY_MODES = ["virtual", "physical", "hybrid"] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export interface Course {
  id: string;
  code: string;          // CR-0001
  title: string;
  description: string;
  tutorId: string;
  mode: DeliveryMode;
  capacity: number;
  prerequisiteId: string | null;
  durationHours: number;
  price: number;         // KES, integer (avoid float for money)
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---- Session ----
export interface Session {
  id: string;
  courseId: string;
  title: string;
  scheduledAt: string;   // ISO-8601
  durationMinutes: number;
  mode: DeliveryMode;
  meetingUrl: string | null;   // Zoom / Meet link
  venue: string | null;        // Physical location
  recordingUrl: string | null; // R2 URL after upload
  createdAt: string;
}

// ---- Enrollment ----
export const PAYMENT_STATUSES = ["pending", "partial", "paid"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  paymentStatus: PaymentStatus;
  enrolledAt: string;
  completedAt: string | null;
}

// ---- Attendance ----
export const ATTENDANCE_STATUSES = ["present", "absent", "excused"] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export interface AttendanceRecord {
  id: string;
  studentId: string;
  sessionId: string;
  status: AttendanceStatus;
  checkedInAt: string | null;
  notes: string | null;
}

// ---- Payment ----
export const PAYMENT_METHODS = ["mpesa", "stripe", "manual"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const TRANSACTION_STATUSES = ["pending", "success", "failed", "refunded"] as const;
export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

export interface Payment {
  id: string;
  code: string;             // PAY-0001
  enrollmentId: string;
  studentId: string;
  amountKes: number;        // Always in KES, integer
  method: PaymentMethod;
  status: TransactionStatus;
  reference: string | null; // M-Pesa receipt / Stripe PI ID
  paidAt: string | null;
  createdAt: string;
}

// ---- Certificate ----
export interface Certificate {
  id: string;
  code: string;             // CERT-0001
  studentId: string;
  courseId: string;
  issuedAt: string;
  r2Key: string;            // R2 storage key — never expose full URL directly
  downloadUrl: string | null; // Signed URL, generated on demand
}

// ---- Admin Snapshot ----
export interface EnrollmentsByMonth {
  month: string;   // "YYYY-MM"
  count: number;
}

export interface AdminSnapshot {
  totalStudents: number;
  totalTutors: number;
  totalCourses: number;
  publishedCourses: number;
  totalEnrollments: number;
  pendingPayments: number;
  pendingCertificates: number;   // paid enrollments not yet completed
  totalRevenueKes: number;
  certificatesIssued: number;
  enrollmentsByMonth: EnrollmentsByMonth[];
}

// ---- API Response envelope ----
export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export interface ApiError {
  ok: false;
  error: string;
  code: string;
  statusCode: number;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ---- Pagination ----
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}
